/**
 * Identity, sessions and multi-office access (WP0-AUTH, BUILD-PLAN 2.6, ADR-0012).
 *
 * Published interface for every other work package:
 * - `PERMISSIONS`: the permission key catalogue (`area.action`). The database
 *   catalogue `identity.permission` is seeded from the same list and a test
 *   keeps them equal. A module that needs a new key adds it here and in a
 *   migration of its own (`insert into identity.permission ... on conflict do nothing`).
 * - `ROLE_TEMPLATES`: the seeded, editable roles every practice starts with.
 * - `SessionInfo`, `OfficeContext`: what `GET /v1/identity/session` and
 *   `GET /v1/identity/context` return; the shell's office selector, permission
 *   hooks and lock screen read these.
 * - Request/response schemas of the identity routes.
 */
import { z } from 'zod';
import { LocationScopeSchema, PermissionKeySchema } from './core.js';
import { PRODUCT } from './product.js';
import { TurnstileTokenSchema } from './turnstile.js';
import { SupportUsageSchema } from './usage-billing.js';
import { CutoverMessageTypeSchema, TextingOwnerSchema } from './warp9-cutover.js';

/* ------------------------------------------------------------------------ */
/* Permission catalogue                                                      */
/* ------------------------------------------------------------------------ */

export interface PermissionDef {
  key: string;
  label: string;
  description: string;
  /** Exposes money, identifiers or account control; shown with a marker in role admin. */
  sensitive?: boolean;
  /**
   * Held by the Owner role only: no other built-in role has it and it cannot
   * be added to a custom role (owner-research-marketplace.md: owners buy).
   */
  ownerOnly?: boolean;
}

/** Permission keys only the Owner role may hold (`PermissionDef.ownerOnly`). */
export const OWNER_ONLY_PERMISSIONS: readonly string[] = ['marketplace.purchase'];

/** Areas in the order the role editor shows them. */
export const PERMISSION_AREAS = [
  { area: 'schedule', label: 'Schedule' },
  { area: 'patient', label: 'Patients' },
  { area: 'chart', label: 'Chart' },
  { area: 'perio', label: 'Perio' },
  { area: 'txplan', label: 'Treatment plans' },
  { area: 'imaging', label: 'Imaging' },
  { area: 'notes', label: 'Clinical notes' },
  { area: 'comms', label: 'Inbox and texting' },
  { area: 'task', label: 'Staff tasks' },
  { area: 'documents', label: 'Patient documents' },
  { area: 'insurance', label: 'Insurance' },
  { area: 'claims', label: 'Claims' },
  { area: 'ledger', label: 'Ledger and payments' },
  { area: 'money', label: 'Money visibility' },
  { area: 'reports', label: 'Analytics and reports' },
  { area: 'analytics', label: 'Patient map' },
  { area: 'marketplace', label: 'Marketplace' },
  { area: 'audit', label: 'Audit log' },
  { area: 'users', label: 'Users' },
  { area: 'roles', label: 'Roles' },
  { area: 'offices', label: 'Offices' },
  { area: 'devices', label: 'Workstations' },
  { area: 'feedback', label: 'Feature requests' },
  { area: 'settings', label: 'Practice settings' },
  { area: 'support', label: 'Support access' },
  { area: 'timeclock', label: 'Timeclock' },
  { area: 'chat', label: 'Team chat' },
  { area: 'hr', label: 'Staff Hub and HR' },
] as const;

export const PERMISSIONS = [
  {
    key: 'schedule.view',
    label: 'View the schedule',
    description: 'See appointments, blockouts and the flow board.',
  },
  {
    key: 'schedule.edit',
    label: 'Book and move appointments',
    description: 'Create, move, resize and change the status of appointments.',
  },
  {
    key: 'schedule.edit_others',
    label: "Edit other providers' columns",
    description: 'Change appointments in columns of providers other than yourself.',
  },
  {
    key: 'schedule.override_conflict',
    label: 'Override schedule conflicts',
    description:
      'Book or move into a slot the office policy blocks (double-booked chair, holiday), with a reason that is audited.',
    sensitive: true,
  },
  {
    key: 'patient.view',
    label: 'View patients',
    description: 'Open patient records, family and contact details.',
  },
  {
    key: 'patient.edit',
    label: 'Edit patients',
    description: 'Change demographics, contact points, alerts and notes.',
  },
  {
    key: 'patient.view_ssn',
    label: 'View full SSN',
    description: 'Reveal full social security numbers.',
    sensitive: true,
  },
  {
    key: 'chart.view',
    label: 'View the chart',
    description: 'See the tooth chart and procedure history.',
  },
  {
    key: 'chart.edit',
    label: 'Chart procedures',
    description: 'Add and edit planned and existing procedures and conditions.',
  },
  {
    key: 'chart.post_complete',
    label: 'Post procedures complete',
    description: 'Set procedures complete, which creates ledger charges.',
  },
  { key: 'perio.view', label: 'View perio exams', description: 'See perio charts and indices.' },
  { key: 'perio.edit', label: 'Record perio exams', description: 'Enter and finish perio exams.' },
  {
    key: 'txplan.view',
    label: 'View treatment plans',
    description: 'See cases, options and estimates.',
  },
  {
    key: 'txplan.edit',
    label: 'Build treatment plans',
    description: 'Create, present and record acceptance of cases.',
  },
  { key: 'imaging.view', label: 'View images', description: 'Open x-rays, photos and mounts.' },
  {
    key: 'imaging.capture',
    label: 'Capture and import images',
    description: 'Acquire, import and annotate images.',
  },
  {
    key: 'notes.view',
    label: 'View clinical notes',
    description: 'Read clinical notes and addenda.',
  },
  {
    key: 'notes.sign',
    label: 'Write and sign notes',
    description: 'Write, sign and lock clinical notes.',
  },
  {
    key: 'comms.view',
    label: 'View conversations',
    description: 'Read texts, calls and the patient timeline.',
  },
  {
    key: 'comms.send',
    label: 'Send messages',
    description: 'Text, email and call patients from the inbox.',
  },
  {
    key: 'task.view',
    label: 'View staff tasks',
    description: "See the office's task lists and the patient contact log.",
  },
  {
    key: 'task.create',
    label: 'Create and work tasks',
    description: 'Add, take, complete and hand off tasks, and log patient contacts.',
  },
  {
    key: 'documents.view',
    label: 'View patient documents',
    description: 'Open scanned forms, letters and other patient documents.',
  },
  {
    key: 'documents.upload',
    label: 'Upload and file documents',
    description: 'Upload, move, rotate, sign, delete and restore patient documents.',
  },
  {
    key: 'documents.legal_hold',
    label: 'Legal holds and retention',
    description: 'Place and release legal holds and run the document retention purge.',
    sensitive: true,
  },
  {
    key: 'insurance.view',
    label: 'View insurance',
    description: 'See plans, benefits and eligibility.',
  },
  {
    key: 'insurance.edit',
    label: 'Edit insurance',
    description: 'Attach plans, edit benefits and run eligibility.',
  },
  { key: 'claims.view', label: 'View claims', description: 'See claims, ERAs and attachments.' },
  {
    key: 'claims.send',
    label: 'Send claims',
    description: 'Create, send and finalize claims.',
    sensitive: true,
  },
  {
    key: 'ledger.view',
    label: 'View ledgers',
    description: 'See patient and family ledgers and statements.',
  },
  {
    key: 'ledger.post_payment',
    label: 'Take payments',
    description: 'Post patient and insurance payments.',
    sensitive: true,
  },
  {
    key: 'ledger.adjust',
    label: 'Adjust balances',
    description: 'Post adjustments, write-offs and transfers.',
    sensitive: true,
  },
  {
    key: 'money.view',
    label: 'See money',
    description: 'Show dollar amounts on schedules, dashboards and reports.',
  },
  {
    key: 'money.close_day',
    label: 'Close the day',
    description:
      "Close and reopen the day's money, approve drawer variances and post into a closed day.",
    sensitive: true,
  },
  {
    key: 'reports.view',
    label: 'View analytics',
    description: 'Open dashboards, the morning huddle and reports.',
  },
  {
    key: 'marketplace.view',
    label: 'Browse the Marketplace',
    description: 'See modules, plans and trials.',
  },
  {
    key: 'marketplace.purchase',
    label: 'Buy and cancel modules',
    description:
      'Start trials, subscribe and cancel paid modules. Owner only; everyone else sends a request.',
    sensitive: true,
    ownerOnly: true,
  },
  {
    key: 'audit.view',
    label: 'View the audit log',
    description: 'Search and export the audit log.',
    sensitive: true,
  },
  {
    key: 'users.view',
    label: 'View users',
    description: 'See the people who can sign in and their access.',
  },
  {
    key: 'users.manage',
    label: 'Manage users',
    description: 'Add people, change their roles and office access, reset MFA and sign them out.',
    sensitive: true,
  },
  {
    key: 'roles.manage',
    label: 'Manage roles',
    description: 'Create roles and change what each role may do.',
    sensitive: true,
  },
  {
    key: 'offices.manage',
    label: 'Manage offices',
    description: 'Add new offices, rename and retire offices.',
    sensitive: true,
  },
  {
    key: 'devices.manage',
    label: 'Manage workstations',
    description: 'Enrol and remove workstations and set their profile.',
  },
  {
    key: 'settings.manage',
    label: 'Manage practice settings',
    description: 'Change practice-wide settings.',
  },
  // Added by WP4-FEEDBACK (migration 202611200400_feedback_requests.sql).
  {
    key: 'feedback.manage',
    label: 'Manage feature requests',
    description: 'Change the status and priority of staff feature requests and bug reports.',
  },
  {
    key: 'support.impersonate',
    label: 'Support sign-in',
    description:
      'Lets Computers2Go support sign in as a user of this practice to help them. Remove this role to refuse support sign-in.',
    sensitive: true,
  },
  // Added by WP2-PERIO (migration 202610101005_perio_amend_permission.sql).
  {
    key: 'perio.amend',
    label: 'Amend finished perio exams',
    description: 'Unlock a finished perio exam with a reason; every change is audited.',
  },
  // Added by NEW-WF-08 (migration 202610310100_chart_clinical_completion.sql).
  {
    key: 'chart.backdate',
    label: 'Chart work on an earlier date',
    description:
      'Post completed procedures with a past date of service, with a reason; every one is audited.',
    sensitive: true,
  },
  {
    key: 'chart.fee_override',
    label: 'Override procedure fees',
    description: 'Change the fee of a procedure charted in Velora; the change is audited.',
    sensitive: true,
  },
  // Added by NEW-WF-09 (migration 202610311210_identity_perio_tx_rights.sql).
  {
    key: 'txplan.plan_perio',
    label: 'Plan perio treatment from own exam',
    description:
      'Add D4341, D4342, D4381 and D4910 to the plan from a perio exam you charted. Other codes need Build treatment plans.',
  },
  {
    key: 'txplan.followup',
    label: 'Work treatment-plan follow-ups',
    description:
      'Log follow-up calls, resend the patient link and request a booking. Does not change the plan.',
  },
  // Added by the treatment-plan presentation (migration 202611221810_identity_txplan_approve_document.sql).
  {
    key: 'txplan.approve_document',
    label: 'Approve treatment-plan presentations',
    description:
      'Review and approve the patient presentation document before it is printed, sent or presented.',
    sensitive: true,
  },
  // Added by the treatment-coordinator role test (migration 202611290750_txplan_tc_roletest.sql).
  {
    key: 'txplan.acceptance_report',
    label: 'Case acceptance report',
    description:
      'See case acceptance by provider, office and month (counts and dollars), without the rest of Analytics.',
  },
  // Added by NEW-WF-06 (migration 202610311000_wf06_schedule_flow_permission.sql).
  {
    key: 'schedule.flow',
    label: 'Move patients through the visit',
    description:
      'Check in, seat, send to checkout and mark Ready for the doctor, without booking or moving appointments.',
  },
  // Added by the office comparison (migration 202611090100_identity_reports_compare.sql).
  {
    key: 'reports.compare',
    label: 'Compare offices',
    description:
      'Rank the offices against each other on production, collections, hygiene, treatment, schedule and A/R.',
  },
  // Added by the timeclock (migration 202611200300_timeclock_schema.sql).
  {
    key: 'timeclock.use',
    label: 'Clock in and out',
    description: 'Clock in and out of the office from the top bar, including breaks.',
  },
  {
    key: 'timeclock.manage',
    label: 'Manage timesheets',
    description:
      "See everyone's hours at the office, add and edit entries with a reason, close forgotten clock-outs and export CSV.",
  },
  // Added by the patient grade (migration 202611221701_grade_permission.sql).
  {
    key: 'patient.grade_manage',
    label: 'Manage patient grades',
    description:
      'Override a patient grade with a reason, restore it to B, and read the quarterly disparity report.',
  },
  // Added by the portal staff preview (migration 202611271500_portal_preview_dormant.sql).
  {
    key: 'patient.portal_preview',
    label: 'View the patient portal as a patient',
    description:
      'Open the real patient portal in a read-only staff preview for one patient. Nothing is sent or saved, and every preview is audited.',
    sensitive: true,
  },
  // Added by Velora Chat (migration 202611222355_chat_permissions.sql).
  {
    key: 'chat.use',
    label: 'Use team chat',
    description:
      'Read and post in the channels of your offices and the practice, and send direct messages.',
  },
  {
    key: 'chat.manage',
    label: 'Manage chat',
    description:
      'Add, rename and archive channels, remove messages, set chat retention and legal holds.',
    sensitive: true,
  },
  {
    key: 'chat.export',
    label: 'Export chat',
    description:
      'Export a channel for a date range after confirming it is you. Every export is audited.',
    sensitive: true,
  },
  // Added by the patient map (migration 202611221500_analytics_patient_map.sql).
  {
    key: 'analytics.patient_map',
    label: 'See patient households on the map',
    description:
      'Exact household dots on Analytics -> Patient Map, the household card (names, last visit, next appointment) and the reactivation list export. Without it the map shows areas only.',
    sensitive: true,
  },
  // Added by the Staff Hub (migration 202611241305_hr_staff_hub.sql).
  {
    key: 'hr.self',
    label: 'Use My Hub',
    description:
      'See your own schedule, hours, licenses, CE, training and documents, and sign the handbook.',
  },
  {
    key: 'hr.view',
    label: 'View staff records',
    description:
      "See the team's licenses, CE, training and sign-offs for your offices, and the compliance grid.",
  },
  {
    key: 'hr.manage',
    label: 'Manage staff records',
    description:
      'Verify licenses, publish the handbook and policies, assign training, run onboarding and offboarding, approve time off and set schedules.',
  },
  {
    key: 'hr.view_sensitive',
    label: 'View sensitive staff records',
    description:
      'Open hepatitis B vaccination and declination records and other medical HR records. Every read is audited.',
    sensitive: true,
  },
  {
    key: 'claims.post_insurance_payment',
    label: 'Enter insurance payments',
    description:
      'Enter insurance payments from EOBs and insurance checks: paid, allowed, write-off, adjustments and deductible per procedure.',
    sensitive: true,
  },
  // Added by the hygienist role test fixes (migration 202611290500_hygiene_roletest.sql).
  {
    key: 'insurance.view_clinical',
    label: 'See the clinical benefits summary',
    description:
      'At the chair: hygiene frequencies used and remaining, next eligible dates and whether the annual maximum has room. No dollar amounts.',
  },
  // Added by the dentist role test fixes (migration 202611290600_dentist_rx_for_others.sql).
  {
    key: 'notes.rx_for_others',
    label: "Print a prescription under another dentist's name",
    description:
      'Print a paper prescription with another dentist as the prescriber (for example for a covering doctor). Every one is audited. Dentists always print their own.',
    sensitive: true,
  },
  // Added by the War Council (migration 202702051100_council_war_council.sql);
  // renamed Office Manager (202702051310_coaching_office_manager.sql).
  {
    key: 'reports.council',
    label: 'Office Manager',
    description:
      "The owner's Office Manager: findings about people, processes and the setup, every person's grade and coaching, notes to staff, the office scoreboard, moves and the playbook.",
    sensitive: true,
  },
] as const satisfies readonly PermissionDef[];

export type Permission = (typeof PERMISSIONS)[number]['key'];
export const PERMISSION_KEYS: readonly Permission[] = PERMISSIONS.map((p) => p.key);

export function isPermission(value: string): value is Permission {
  return (PERMISSION_KEYS as readonly string[]).includes(value);
}

/** Permissions of one area, in catalogue order. */
export function permissionsInArea(area: string): PermissionDef[] {
  return PERMISSIONS.filter((p) => p.key.startsWith(`${area}.`));
}

/* ------------------------------------------------------------------------ */
/* Seeded roles (BUILD-PLAN 2.6)                                             */
/* ------------------------------------------------------------------------ */

export interface RoleTemplate {
  key: string;
  name: string;
  description: string;
  /** Members must use MFA (BUILD-PLAN 2.6: Owner, Manager, Billing roles). */
  mfaRequired: boolean;
  /** Members of this role get every office, including ones added later. */
  allOffices: boolean;
  /** Only meaningful for Computers2Go support accounts. */
  supportOnly: boolean;
  permissions: readonly Permission[];
}

/**
 * Every read-only key (`*.view`), including the audit log and the user list,
 * except staff HR records (`hr.view`): those stay with the owner and office
 * manager.
 */
const VIEW_ALL: readonly Permission[] = PERMISSION_KEYS.filter(
  (k) => k.endsWith('.view') && k !== 'hr.view',
);

const without = (list: readonly Permission[], drop: readonly Permission[]) =>
  list.filter((k) => !drop.includes(k));

export const ROLE_TEMPLATES: readonly RoleTemplate[] = [
  {
    key: 'owner',
    name: 'Owner',
    description: 'Everything, in every office, including adding offices and buying modules.',
    mfaRequired: true,
    allOffices: true,
    supportOnly: false,
    permissions: without(PERMISSION_KEYS, ['support.impersonate']),
  },
  {
    key: 'office_manager',
    name: 'Office Manager',
    description: 'Runs the office: schedule, patients, money, users and workstations.',
    mfaRequired: true,
    allOffices: false,
    supportOnly: false,
    permissions: without(PERMISSION_KEYS, [
      'support.impersonate',
      'marketplace.purchase',
      'offices.manage',
      'roles.manage',
      'patient.view_ssn',
      'txplan.approve_document',
      'chat.export',
      'hr.view_sensitive',
      'reports.council',
    ]),
  },
  {
    key: 'front_desk',
    name: 'Front Desk',
    description: 'Books and checks in patients, takes payments, answers the inbox.',
    mfaRequired: false,
    allOffices: false,
    supportOnly: false,
    permissions: [
      'hr.self',
      'task.view',
      'task.create',
      'timeclock.use',
      'documents.view',
      'documents.upload',
      'schedule.view',
      'schedule.edit',
      'schedule.edit_others',
      'schedule.flow',
      'patient.view',
      'patient.edit',
      'chart.view',
      'txplan.view',
      'txplan.followup',
      'comms.view',
      'comms.send',
      'insurance.view',
      'insurance.edit',
      'ledger.view',
      'ledger.post_payment',
      'money.view',
      'chat.use',
    ],
  },
  {
    key: 'hygienist',
    name: 'Hygienist',
    description: 'Charts, perio, images and notes for their own patients.',
    mfaRequired: false,
    allOffices: false,
    supportOnly: false,
    permissions: [
      'hr.self',
      'task.view',
      'task.create',
      'timeclock.use',
      'documents.view',
      'documents.upload',
      'schedule.view',
      'schedule.edit',
      'schedule.flow',
      'patient.view',
      'chart.view',
      'chart.edit',
      'chart.post_complete',
      'perio.view',
      'perio.edit',
      'perio.amend',
      'txplan.view',
      'txplan.plan_perio',
      'insurance.view_clinical',
      'imaging.view',
      'imaging.capture',
      'notes.view',
      'notes.sign',
      'comms.view',
      'chat.use',
    ],
  },
  {
    key: 'dentist',
    name: 'Dentist',
    description: 'Clinical care, treatment planning and production reports.',
    mfaRequired: false,
    allOffices: false,
    supportOnly: false,
    permissions: [
      'hr.self',
      'task.view',
      'task.create',
      'timeclock.use',
      'documents.view',
      'documents.upload',
      'schedule.view',
      'schedule.edit',
      'schedule.flow',
      'patient.view',
      'patient.edit',
      'chart.view',
      'chart.edit',
      'chart.post_complete',
      'chart.backdate',
      'chart.fee_override',
      'perio.view',
      'perio.edit',
      'perio.amend',
      'txplan.view',
      'txplan.edit',
      'txplan.approve_document',
      'txplan.acceptance_report',
      'imaging.view',
      'imaging.capture',
      'notes.view',
      'notes.sign',
      'comms.view',
      'insurance.view',
      'insurance.view_clinical',
      'ledger.view',
      'money.view',
      'reports.view',
      'chat.use',
    ],
  },
  {
    key: 'assistant',
    name: 'Assistant',
    description: 'Seats patients, charts for the doctor, captures images.',
    mfaRequired: false,
    allOffices: false,
    supportOnly: false,
    permissions: [
      'hr.self',
      'task.view',
      'task.create',
      'timeclock.use',
      'documents.view',
      'documents.upload',
      'schedule.view',
      'schedule.flow',
      'patient.view',
      'chart.view',
      'chart.edit',
      'perio.view',
      'perio.edit',
      'txplan.view',
      'insurance.view_clinical',
      'imaging.view',
      'imaging.capture',
      'notes.view',
      'chat.use',
    ],
  },
  {
    key: 'billing',
    name: 'Billing',
    description: 'Claims, ERAs, ledgers, statements and collections.',
    mfaRequired: true,
    allOffices: false,
    supportOnly: false,
    permissions: [
      'hr.self',
      'task.view',
      'task.create',
      'timeclock.use',
      'documents.view',
      'schedule.view',
      'patient.view',
      'chart.view',
      'txplan.view',
      'comms.view',
      'comms.send',
      'insurance.view',
      'insurance.edit',
      'claims.view',
      'claims.send',
      'claims.post_insurance_payment',
      'ledger.view',
      'ledger.post_payment',
      'ledger.adjust',
      'money.view',
      'reports.view',
      'chat.use',
    ],
  },
  {
    key: 'treatment_coordinator',
    name: 'Treatment Coordinator',
    description: 'Presents treatment, estimates and financing, and books the work.',
    mfaRequired: false,
    allOffices: false,
    supportOnly: false,
    permissions: [
      'hr.self',
      'task.view',
      'task.create',
      'timeclock.use',
      'documents.view',
      'documents.upload',
      'schedule.view',
      'schedule.edit',
      'patient.view',
      'patient.edit',
      'chart.view',
      'txplan.view',
      'txplan.edit',
      'txplan.acceptance_report',
      'imaging.view',
      'comms.view',
      'comms.send',
      'insurance.view',
      'ledger.view',
      'money.view',
      'chat.use',
    ],
  },
  {
    key: 'billing_admin',
    name: 'Billing Admin',
    description: 'Sees Marketplace billing, invoices and usage, and asks the owner to add modules.',
    mfaRequired: true,
    allOffices: false,
    supportOnly: false,
    permissions: ['marketplace.view', 'money.view', 'reports.view', 'chat.use'],
  },
  {
    key: 'auditor',
    name: 'Read-only Auditor',
    description: 'Can look at everything and the audit log, and change nothing.',
    mfaRequired: false,
    allOffices: false,
    supportOnly: false,
    permissions: VIEW_ALL,
  },
  {
    // ADM-24: the login automated audits and checks use, so their reads are
    // not attributed to (and do not drown out) the real owner. Opens every
    // screen with view permissions only and cannot export the audit log
    // (audit.view). Some POST routes are gated by *.view, so it is not a hard
    // read-only lock (docs/requests/V26-read-only-role-enforcement.md).
    key: 'automated_audit',
    name: 'Automated Audit (read-only)',
    description:
      'For automated checks of the app: view permissions only, so no changes to settings, patients, the schedule or money, and no audit-log export. Not a hard read-only lock yet: a few view-level actions (such as logging a call attempt) still work.',
    mfaRequired: true,
    allOffices: false,
    supportOnly: false,
    permissions: without(VIEW_ALL, ['audit.view']),
  },
  {
    key: 'c2g_support',
    name: 'C2G Support',
    description:
      'Computers2Go support staff. Grants nothing on its own; it allows support to sign in as a user of this practice. Remove it to refuse support sign-in.',
    mfaRequired: true,
    allOffices: false,
    supportOnly: true,
    permissions: ['support.impersonate'],
  },
  {
    // Velora Chat: the practice's managed-service (IT) technicians. Team chat
    // only (#it-help and direct messages); no patient or chart access, and
    // patient references in chat never resolve for this role.
    key: 'msp',
    name: 'MSP Technician',
    description:
      'IT support from your managed service provider: team chat in #it-help and direct messages only. No patient access.',
    mfaRequired: true,
    allOffices: false,
    supportOnly: false,
    permissions: ['chat.use'],
  },
];

/* ------------------------------------------------------------------------ */
/* Shared shapes                                                             */
/* ------------------------------------------------------------------------ */

/** Office colour token (`office-1`..`office-12`), stable per office in every chart. */
export const OfficeColorTokenSchema = z.string().regex(/^office-([1-9]|1[0-2])$/);

export const OfficeSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  shortName: z.string(),
  colorToken: OfficeColorTokenSchema,
  timezone: z.string(),
  active: z.boolean(),
  sortOrder: z.number().int(),
});
export type OfficeSummary = z.infer<typeof OfficeSummarySchema>;

/** 0-based chart colour slot for an office colour token (`office-3` -> 2). */
export function officeColorIndex(token: string): number {
  const m = /^office-(\d+)$/.exec(token);
  return m ? Math.max(0, Number(m[1]) - 1) : 0;
}

export const DeviceProfileSchema = z.enum([
  'front_desk',
  'operatory',
  'imaging',
  'kiosk',
  'personal',
]);
export type DeviceProfile = z.infer<typeof DeviceProfileSchema>;

/**
 * Idle lock per device profile. Owner decision 2026-11-01: sign out only after
 * 2 hours without use (was 15-30 min). Kiosks stay short; a device's own
 * idle_lock_minutes still overrides.
 */
export const IDLE_LOCK_SECONDS: Record<DeviceProfile | 'unknown', number> = {
  front_desk: 2 * 60 * 60,
  operatory: 2 * 60 * 60,
  imaging: 2 * 60 * 60,
  kiosk: 5 * 60,
  personal: 2 * 60 * 60,
  unknown: 2 * 60 * 60,
};

/** Absolute session lifetime: inactivity is the limit, so a working day never cuts a session. */
export const SESSION_ABSOLUTE_SECONDS = 24 * 60 * 60;
/** Support ("sign in as") sessions: same rule, still audited under both names. */
export const IMPERSONATION_SESSION_SECONDS = 12 * 60 * 60;

export const COOKIE_SESSION = 'velora_session';
export const COOKIE_CSRF = 'velora_csrf';
export const COOKIE_DEVICE = 'velora_device';
/**
 * "Remember this device": random 256-bit token, HttpOnly, Secure,
 * SameSite=Strict. Skips only the second factor at sign-in, never the
 * password and never step-up re-authentication.
 */
export const COOKIE_REMEMBER = 'velora_remember';
/**
 * While support is signed in as a practice user from the C2G Admin console,
 * the support account's own session token waits here (HttpOnly,
 * SameSite=Strict, sent only to /v1/identity/impersonation/end) so "Return
 * to my account" can bring it back.
 */
export const COOKIE_SUPPORT_RETURN = 'velora_support_return';
/** Remembered devices last a fixed 30 days from the sign-in that created them. */
export const REMEMBER_DEVICE_DAYS = 30;
export const REMEMBER_DEVICE_SECONDS = REMEMBER_DEVICE_DAYS * 24 * 60 * 60;
/** Header carrying the CSRF token on unsafe cookie-authenticated requests. */
export const CSRF_HEADER = 'x-csrf-token';
/** Requests with this header set to `1` do not count as user activity for the idle lock. */
export const BACKGROUND_HEADER = 'x-velora-background';

/**
 * Why a request was refused with 401 `unauthenticated`. The client branches
 * on `error.details.reason`.
 */
export const AuthRefusalReasonSchema = z.enum([
  'no_session',
  'expired',
  'revoked',
  'mfa_required',
  'mfa_enrollment_required',
  'session_locked',
  'csrf',
]);
export type AuthRefusalReason = z.infer<typeof AuthRefusalReasonSchema>;

/** The office context consumed by the shell's office selector (ADR-0012). */
export const OfficeContextSchema = z.object({
  /** Offices the user may access, active first, in practice order. */
  offices: z.array(OfficeSummarySchema),
  /** The selected view: one office or all accessible offices. */
  scope: LocationScopeSchema.nullable(),
  /** Where the user is working (attribution for new payments, check-ins). */
  workingLocationId: z.uuid().nullable(),
  /** False when the user has a single office: "All offices" is not offered. */
  allowAll: z.boolean(),
  /** True when the user may add offices (shows "Add an office" in the selector). */
  canAddOffice: z.boolean(),
});
export type OfficeContext = z.infer<typeof OfficeContextSchema>;

export const UpdateOfficeContextSchema = z
  .object({
    scope: LocationScopeSchema.optional(),
    workingLocationId: z.uuid().nullable().optional(),
  })
  .refine((v) => v.scope !== undefined || v.workingLocationId !== undefined, {
    message: 'Send a scope, a working office, or both.',
  });
export type UpdateOfficeContext = z.infer<typeof UpdateOfficeContextSchema>;

export const SessionInfoSchema = z.object({
  user: z.object({
    id: z.uuid(),
    displayName: z.string(),
    email: z.string(),
    /** A Computers2Go support account (opens the C2G Admin console). */
    isSupport: z.boolean().optional(),
  }),
  practice: z.object({ id: z.uuid(), name: z.string(), slug: z.string() }),
  /** Other practices this person can sign in to (DSO staff, support). */
  practices: z.array(z.object({ id: z.uuid(), name: z.string(), slug: z.string() })),
  context: OfficeContextSchema,
  /** Practice-wide permission keys. */
  permissions: z.array(PermissionKeySchema),
  /** Offices where the user's permissions differ from `permissions` (per-office roles). */
  locationPermissions: z.record(z.uuid(), z.array(PermissionKeySchema)),
  roles: z.array(
    z.object({ id: z.uuid(), key: z.string(), name: z.string(), locationId: z.uuid().nullable() }),
  ),
  impersonator: z.object({ userId: z.uuid(), displayName: z.string() }).nullable(),
  /** Modules hidden from this person by their roles (`ROLE_MODULES`). */
  hiddenModules: z.array(z.string()).optional(),
  mfa: z.object({ enrolled: z.boolean(), required: z.boolean() }),
  session: z.object({
    id: z.uuid(),
    expiresAt: z.string(),
    idleLockSeconds: z.number().int().positive(),
    hasPin: z.boolean(),
    device: z.object({ id: z.uuid(), name: z.string(), profile: DeviceProfileSchema }).nullable(),
  }),
  /** The CSRF token for unsafe requests (also in the `velora_csrf` cookie). */
  csrfToken: z.string(),
});
export type SessionInfo = z.infer<typeof SessionInfoSchema>;

/* ------------------------------------------------------------------------ */
/* Login, MFA, lock                                                          */
/* ------------------------------------------------------------------------ */

export const EmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Enter an email address.');

/** Passwords: length over composition rules (NIST 800-63B). */
export const NewPasswordSchema = z
  .string()
  .min(12, 'Use at least 12 characters.')
  .max(200, 'Use at most 200 characters.');

/**
 * Staff usernames (2026-10-28): `first.last`, lowercase ASCII letters and
 * digits joined by `.`, `-` or `_`, 2 to 40 characters, unique across all of
 * Velora (`mary.smith`, `mary.smith2` on a clash).
 */
export const USERNAME_PATTERN = /^[a-z0-9]+([._-][a-z0-9]+)*$/;
export const UsernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2, 'Use at least 2 characters.')
  .max(40, 'Use at most 40 characters.')
  .regex(
    USERNAME_PATTERN,
    'Use lowercase letters and numbers, joined by a dot, dash or underscore (mary.smith).',
  );

const FOLD: Record<string, string> = {
  ß: 'ss',
  æ: 'ae',
  œ: 'oe',
  ø: 'o',
  ł: 'l',
  đ: 'd',
  ð: 'd',
  þ: 'th',
  ı: 'i',
};

/** One name part as username ASCII: accents stripped, only a-z and 0-9 kept. */
export function usernamePart(s: string | null | undefined): string {
  return (s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[ßæœøłđðþı]/g, (c) => FOLD[c] ?? '')
    .replace(/[^a-z0-9]+/g, '');
}

const NAME_SUFFIX = /^(jr|sr|ii|iii|iv|dds|dmd|rdh|cda|rda|efda|md|phd|ms)\.?$/i;

/** Name words without credentials and generational suffixes ("Smith Jr., DDS" -> Smith). */
function nameWords(s: string | null | undefined): string[] {
  const words = ((s ?? '').split(',')[0] ?? '').split(/\s+/).filter(Boolean);
  while (words.length > 1 && NAME_SUFFIX.test(words[words.length - 1] ?? '')) words.pop();
  return words;
}

/**
 * `first.last` for a person (without the clash number); `staff` when both
 * are empty. Only the first word of the first name ("Mary Ann" -> mary);
 * the whole last name ("Van Dyke" -> vandyke), without "Jr" or "DDS".
 */
export function usernameFromName(
  first: string | null | undefined,
  last: string | null | undefined,
) {
  const f = usernamePart(nameWords(first)[0]);
  const l = usernamePart(nameWords(last).join(''));
  const base = f && l ? `${f}.${l}` : f || l || 'staff';
  return base.slice(0, 36).replace(/[._-]+$/, '') || 'staff';
}

const NAME_TITLES = /^(dr|doctor|mr|mrs|ms|miss|mx)\.?$/i;

/** First and last name from a display name ("Dr. Mary Ann Smith, DDS" -> Mary / Smith). */
export function splitPersonName(displayName: string): { first: string; last: string } {
  const words = nameWords(displayName).filter((w) => !NAME_TITLES.test(w));
  const first = words[0] ?? '';
  return { first, last: words.length > 1 ? (words[words.length - 1] ?? '') : '' };
}

/** What the sign-in box takes: an email (has an @) or a username. */
export const LoginIdSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .refine(
    (v) => (v.includes('@') ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) : USERNAME_PATTERN.test(v)),
    {
      message: 'Enter your username or email.',
    },
  );

export const LoginRequestSchema = z.object({
  /** The username OR the email the person signs in with (the field name is kept for older apps). */
  email: LoginIdSchema,
  password: z.string().min(1).max(200),
  /** Practice to sign in to, when the person belongs to more than one. */
  practiceSlug: z.string().max(63).optional(),
  /** Cloudflare Turnstile token when sign-in is protected (docs/security/turnstile.md). */
  turnstileToken: TurnstileTokenSchema,
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/**
 * Where the person is in the sign-in flow. A first sign-in with a temporary
 * password (2026-11-01) goes `password_change_required`, then
 * `email_required` (when the sign-in email is a placeholder), then
 * `mfa_enrollment_required`, then `ok`.
 */
export const LoginStatusSchema = z.enum([
  'ok',
  'mfa_required',
  'mfa_enrollment_required',
  'password_change_required',
  'email_required',
]);
export type LoginStatus = z.infer<typeof LoginStatusSchema>;

/** First sign-in step left before two-step set-up (`/session/peek`). */
export const FirstSignInStepSchema = z.enum(['password', 'email']);
export type FirstSignInStep = z.infer<typeof FirstSignInStepSchema>;

/** `POST /v1/identity/login/first/password`: the temporary password, then the person's own. */
export const FirstSignInPasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: NewPasswordSchema,
});
export type FirstSignInPasswordRequest = z.infer<typeof FirstSignInPasswordRequestSchema>;

/** `POST /v1/identity/login/first/email`: the person's own email in place of the placeholder. */
export const FirstSignInEmailRequestSchema = z.object({ email: EmailSchema });
export type FirstSignInEmailRequest = z.infer<typeof FirstSignInEmailRequestSchema>;

export const LoginResponseSchema = z.object({
  status: LoginStatusSchema,
  displayName: z.string(),
  practiceName: z.string(),
  csrfToken: z.string(),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/** A 6-digit authenticator code or a recovery code (`xxxxx-xxxxx`). */
export const MfaCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^(\d{6}|[a-z0-9]{5}-?[a-z0-9]{5})$/i, 'Enter the 6-digit code or a recovery code.'),
});
export type MfaCode = z.infer<typeof MfaCodeSchema>;

/**
 * Two-step sign-in methods (2026-11-02): the authenticator app (TOTP), a
 * 6-digit code by text message, or a 6-digit code by email. Recovery codes
 * are always a fallback, not a method.
 */
export const MfaMethodKindSchema = z.enum(['totp', 'sms', 'email']);
export type MfaMethodKind = z.infer<typeof MfaMethodKindSchema>;
export const MFA_METHOD_KINDS = MfaMethodKindSchema.options;
/** The methods that send a code. */
export const MfaSentMethodSchema = z.enum(['sms', 'email']);
export type MfaSentMethod = z.infer<typeof MfaSentMethodSchema>;

export const MFA_METHOD_LABELS: Record<MfaMethodKind, string> = {
  totp: 'Authenticator app',
  sms: 'Text message',
  email: 'Email code',
};

/** A texted or emailed code works for 5 minutes. */
export const MFA_SENT_CODE_SECONDS = 5 * 60;
/** The sign-in text. `{code}` is replaced. */
export const MFA_SMS_TEMPLATE = `${PRODUCT.name} code: {code}. It expires in 5 minutes. Don't share it.`;

/** The sign-in second-factor step; `rememberDevice` is the "Remember this device for 30 days" box. */
export const LoginMfaRequestSchema = MfaCodeSchema.extend({
  rememberDevice: z.boolean().optional(),
  /**
   * Which code this is. Absent: 6 digits are the authenticator, anything else
   * a recovery code (older apps).
   */
  method: z.enum(['totp', 'sms', 'email', 'recovery']).optional(),
});
export type LoginMfaRequest = z.infer<typeof LoginMfaRequestSchema>;

export const MfaEnrollmentSchema = z.object({
  /** Base32 secret, shown for manual entry. */
  secret: z.string(),
  /** `otpauth://` URI for the QR code. */
  otpauthUri: z.string(),
  /** True when this is the pending code shown earlier (reload, sign in again). */
  reused: z.boolean().optional(),
});
export type MfaEnrollment = z.infer<typeof MfaEnrollmentSchema>;

/** Starting enrolment: `restart` throws away the pending code and makes a new one. */
export const MfaEnrollRequestSchema = z.object({ restart: z.boolean().optional() }).nullish();
export type MfaEnrollRequest = z.infer<typeof MfaEnrollRequestSchema>;

/** Shown when an enrolment code does not match the pending secret. */
export const MFA_ENROLL_WRONG_CODE_MESSAGE = `That code did not work. If you scanned an earlier code, delete old ${PRODUCT.shortName} entries in your authenticator and scan this one.`;

export const MfaConfirmResponseSchema = z.object({
  status: z.literal('ok'),
  /** Shown once; each works once if the authenticator is lost. */
  recoveryCodes: z.array(z.string()),
  csrfToken: z.string(),
});
export type MfaConfirmResponse = z.infer<typeof MfaConfirmResponseSchema>;

/** One way the person can get a code, as the sign-in and My sign-in screens show it. */
export const MfaMethodViewSchema = z.object({
  kind: MfaMethodKindSchema,
  /** Masked destination: `•••-•••-6887`, `b•••@yahoo.com`; null for the authenticator. */
  hint: z.string().nullable(),
});
export type MfaMethodView = z.infer<typeof MfaMethodViewSchema>;

/** `GET /v1/identity/login/mfa/options`: the code step and its "Try another way". */
export const MfaSignInOptionsSchema = z.object({
  /** The method the code step opens with. */
  defaultMethod: MfaMethodKindSchema,
  /** Every usable method, the default first. */
  methods: z.array(MfaMethodViewSchema),
  /** Recovery codes left (the person may use one). */
  recoveryCodes: z.boolean(),
});
export type MfaSignInOptions = z.infer<typeof MfaSignInOptionsSchema>;

/** `POST /v1/identity/login/mfa/send`: text or email a sign-in code. */
export const MfaSendRequestSchema = z.object({ method: MfaSentMethodSchema });
export type MfaSendRequest = z.infer<typeof MfaSendRequestSchema>;

export const MfaSendResponseSchema = z.object({
  /** False: a code went out a moment ago and still works (not sent again). */
  sent: z.boolean(),
  hint: z.string(),
  expiresInSeconds: z.number().int(),
  /** Seconds before another code can be asked for. */
  retryAfterSeconds: z.number().int().optional(),
});
export type MfaSendResponse = z.infer<typeof MfaSendResponseSchema>;

/** `GET /v1/identity/login/mfa/setup`: the methods this person may set up. */
export const MfaSetupOptionsSchema = z.object({
  allowed: z.array(MfaMethodKindSchema),
  /** The person's own sign-in email (null while a placeholder). */
  accountEmail: z.string().nullable(),
});
export type MfaSetupOptions = z.infer<typeof MfaSetupOptionsSchema>;

/** Starts proving a mobile number or an email: a code is sent to it. */
export const MfaContactStartRequestSchema = z.object({
  kind: MfaSentMethodSchema,
  /** A US mobile number (any format) or an email address. */
  destination: z.string().trim().min(3).max(254),
});
export type MfaContactStartRequest = z.infer<typeof MfaContactStartRequestSchema>;

export const MfaContactConfirmRequestSchema = z.object({
  kind: MfaSentMethodSchema,
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter the 6-digit code.'),
});
export type MfaContactConfirmRequest = z.infer<typeof MfaContactConfirmRequestSchema>;

/** My sign-in: the method sign-in starts with. */
export const MfaDefaultRequestSchema = z.object({ method: MfaMethodKindSchema });

export const MfaStatusSchema = z.object({
  enrolled: z.boolean(),
  required: z.boolean(),
  /** When the authenticator app was set up (null: no authenticator). */
  confirmedAt: z.string().nullable(),
  recoveryCodesLeft: z.number().int(),
  /** Every method set up (absent from older servers). */
  methods: z.array(MfaMethodViewSchema).optional(),
  defaultMethod: MfaMethodKindSchema.nullable().optional(),
  /** The methods the practice lets this person use. */
  allowed: z.array(MfaMethodKindSchema).optional(),
});
export type MfaStatus = z.infer<typeof MfaStatusSchema>;

export const SessionPeekSchema = z.object({
  state: z.enum(['none', 'mfa_pending', 'mfa_enroll', 'locked', 'active']),
  displayName: z.string().nullable(),
  practiceName: z.string().nullable(),
  hasPin: z.boolean(),
  /** `mfa_enroll` sessions of a first sign-in: the step before two-step set-up. */
  setupStep: FirstSignInStepSchema.nullable().optional(),
});
export type SessionPeek = z.infer<typeof SessionPeekSchema>;

export const PinSchema = z.string().regex(/^\d{4,8}$/, 'Use 4 to 8 digits.');

export const UnlockRequestSchema = z
  .object({ pin: PinSchema.optional(), password: z.string().min(1).max(200).optional() })
  .refine((v) => (v.pin === undefined) !== (v.password === undefined), {
    message: 'Send a PIN or a password.',
  });
export type UnlockRequest = z.infer<typeof UnlockRequestSchema>;

export const SetPinRequestSchema = z.object({
  pin: PinSchema,
  password: z.string().min(1).max(200),
});
export const ChangePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: NewPasswordSchema,
});
export const DisableMfaRequestSchema = z.object({ password: z.string().min(1).max(200) });

export const OkSchema = z.object({ ok: z.literal(true) });

/* ------------------------------------------------------------------------ */
/* Impersonation (BUILD-PLAN 2.6)                                            */
/* ------------------------------------------------------------------------ */

/**
 * Claims of a one-shot impersonation link from the C2G control plane.
 * Token format: `base64url(json claims) + "." + base64url(HMAC-SHA256(secret, first part))`.
 * `act` follows RFC 8693: the support user acting on behalf of `sub`.
 */
export const ImpersonationClaimsSchema = z.object({
  jti: z.string().min(16).max(128),
  iss: z.literal('c2g-control'),
  /** Target user id. */
  sub: z.uuid(),
  practiceId: z.uuid(),
  act: z.object({ sub: z.uuid() }),
  /** Unix seconds. */
  iat: z.number().int(),
  exp: z.number().int(),
  /** Ticket or reason, recorded in the audit log. */
  reason: z.string().min(3).max(500),
});
export type ImpersonationClaims = z.infer<typeof ImpersonationClaimsSchema>;

/** Longest accepted link lifetime. */
export const IMPERSONATION_LINK_MAX_SECONDS = 60;

export const AcceptImpersonationSchema = z.object({ token: z.string().min(20).max(4096) });

/* ------------------------------------------------------------------------ */
/* C2G Admin: the support console (support accounts only)                    */
/* ------------------------------------------------------------------------ */

/** Signed path of the console (bare page, outside the practice shell). */
export const SUPPORT_ADMIN_PATH = '/support/admin';

export const SupportPracticeSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  status: z.string(),
  memberCount: z.number().int(),
  offices: z.array(
    z.object({ id: z.uuid(), name: z.string(), shortName: z.string(), active: z.boolean() }),
  ),
});
export type SupportPractice = z.infer<typeof SupportPracticeSchema>;

export const SupportMemberSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  email: z.string(),
  status: z.enum(['active', 'disabled']),
  isSupport: z.boolean(),
  roles: z.array(z.string()),
  officeIds: z.array(z.uuid()),
  allOffices: z.boolean(),
  lastSignInAt: z.string().nullable(),
  mfaEnrolled: z.boolean(),
  /** Support may sign in as this person (active, not a support account). */
  canSignInAs: z.boolean(),
});
export type SupportMember = z.infer<typeof SupportMemberSchema>;

export const SupportAccessEntrySchema = z.object({
  occurredAt: z.string(),
  action: z.enum(['link_minted', 'session_started', 'session_ended', 'refused', 'account_action']),
  practiceName: z.string().nullable(),
  targetName: z.string().nullable(),
  reason: z.string().nullable(),
  detail: z.string().nullable(),
});
export type SupportAccessEntry = z.infer<typeof SupportAccessEntrySchema>;

export const SupportImpersonateRequestSchema = z.object({
  practiceId: z.uuid(),
  userId: z.uuid(),
  /** Ticket number or why; recorded in both audit logs. */
  reason: z.string().trim().min(5, 'Say why (a ticket number or a few words).').max(500),
});
export type SupportImpersonateRequest = z.infer<typeof SupportImpersonateRequestSchema>;

/** A one-shot link token (valid IMPERSONATION_LINK_MAX_SECONDS) for /impersonation/accept. */
export const SupportImpersonateResponseSchema = z.object({
  token: z.string(),
  expiresAt: z.string(),
});
export type SupportImpersonateResponse = z.infer<typeof SupportImpersonateResponseSchema>;

/** A PulseSync office id Computers2Go assigned to a practice (meta.pulsesync_office_assignment). */
export const SupportPulsesyncOfficeSchema = z.object({
  pulsesyncOfficeId: z.string(),
  note: z.string().nullable(),
  assignedAt: z.string(),
  /** The office linked to it, if any. */
  linkedLocationId: z.uuid().nullable(),
});
export type SupportPulsesyncOffice = z.infer<typeof SupportPulsesyncOfficeSchema>;

/**
 * Who texts an office's patients (Warp 9 is folding into Velora). `state`:
 *   velora_live   Velora owns at least one message type and can send (texting
 *                 channel and line on, automations on)
 *   warp9_shadow  Warp 9 sends; Velora shadows and compares (sends nothing)
 *   warp9         Warp 9 sends; shadow mode is off
 *   off           not on Warp 9, and Velora texting is not on
 */
export const SupportTextingStateSchema = z.enum(['velora_live', 'warp9_shadow', 'warp9', 'off']);
export type SupportTextingState = z.infer<typeof SupportTextingStateSchema>;

export const SupportOfficeStatusSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  shortName: z.string(),
  active: z.boolean(),
  timezone: z.string(),
  pms: z
    .object({
      adapter: z.string(),
      pmsSystem: z.string(),
      pmsVersion: z.string().nullable(),
      pulsesyncOfficeId: z.string().nullable(),
      clinicNum: z.string().nullable(),
      writesPaused: z.boolean(),
      /** Another office whose PMS patients this office shares (one shared install). */
      sharesPatientsWith: z.uuid().nullable(),
      lastSyncedAt: z.string().nullable(),
      syncErrors: z.number().int(),
    })
    .nullable(),
  texting: z.object({
    state: SupportTextingStateSchema,
    /** VELORA_WARP9_CUTOVER on this server; while off, Warp 9 keeps every type of a linked office. */
    cutoverEnabled: z.boolean(),
    warp9: z
      .object({
        tenant: z.string(),
        shadowEnabled: z.boolean(),
        shadowStartedAt: z.string().nullable(),
      })
      .nullable(),
    /** Effective owner of each message type (texting_owner and the server flag). */
    owners: z.record(CutoverMessageTypeSchema, TextingOwnerSchema),
    channel: z
      .object({
        smsProvider: z.string(),
        smsEnabled: z.boolean(),
        fromNumber: z.string().nullable(),
        emailEnabled: z.boolean(),
      })
      .nullable(),
    /** Inbox lines that may send texts. */
    smsLines: z.number().int(),
    automation: z.object({
      practiceMasterOn: z.boolean(),
      officeMasterOn: z.boolean(),
      reminders: z.boolean(),
      recall: z.boolean(),
      patientTexts: z.boolean(),
    }),
  }),
});
export type SupportOfficeStatus = z.infer<typeof SupportOfficeStatusSchema>;

export const SupportOfficeStatusListSchema = z.object({
  offices: z.array(SupportOfficeStatusSchema),
  pulsesyncOffices: z.array(SupportPulsesyncOfficeSchema),
});
export type SupportOfficeStatusList = z.infer<typeof SupportOfficeStatusListSchema>;

export const SupportRoleSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  allOffices: z.boolean(),
  mfaRequired: z.boolean(),
});
export type SupportRole = z.infer<typeof SupportRoleSchema>;

/**
 * Adding a person from C2G Admin. A new account gets either a temporary
 * password support hands over, or a one-time set-password link (shown once).
 * An email that already signs in at another practice is shared and keeps its
 * password.
 */
export const SupportCreateMemberRequestSchema = z.object({
  email: EmailSchema,
  displayName: z.string().trim().min(1).max(120),
  credential: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('password'), password: NewPasswordSchema }),
    z.object({ kind: z.literal('link') }),
  ]),
  roleIds: z.array(z.uuid()).min(1, 'Give the person at least one role.'),
  allOffices: z.boolean().default(false),
  locationIds: z.array(z.uuid()).default([]),
});
export type SupportCreateMemberRequest = z.input<typeof SupportCreateMemberRequestSchema>;

export const SupportCreateMemberResponseSchema = z.object({
  userId: z.uuid(),
  /** False: the email already signed in elsewhere; its password is unchanged. */
  created: z.boolean(),
  /** Path and fragment of the one-time set-password link (credential kind `link`), shown once. */
  setupPath: z.string().nullable(),
  setupExpiresAt: z.string().nullable(),
});
export type SupportCreateMemberResponse = z.infer<typeof SupportCreateMemberResponseSchema>;

export const PracticeSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9][a-z0-9-]{1,62}$/, 'Lowercase letters, digits and dashes (2 to 63).');

export const SupportCreatePracticeRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  slug: PracticeSlugSchema,
  timezone: z.string().min(3).max(64),
  officeName: z.string().trim().min(1).max(120),
  officeShortName: z.string().trim().min(1).max(24),
  ownerEmail: EmailSchema,
  ownerName: z.string().trim().min(1).max(120),
  /** Needed for a new owner account; ignored when the email already signs in elsewhere. */
  ownerPassword: NewPasswordSchema.optional(),
});
export type SupportCreatePracticeRequest = z.input<typeof SupportCreatePracticeRequestSchema>;

export const SupportCreatePracticeResponseSchema = z.object({
  practiceId: z.uuid(),
  locationId: z.uuid(),
  ownerUserId: z.uuid(),
  ownerCreated: z.boolean(),
});
export type SupportCreatePracticeResponse = z.infer<typeof SupportCreatePracticeResponseSchema>;

export const SupportAddOfficeRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  shortName: z.string().trim().min(1).max(24),
  timezone: z.string().min(3).max(64),
});
export type SupportAddOfficeRequest = z.infer<typeof SupportAddOfficeRequestSchema>;

export const SupportAssignPulsesyncRequestSchema = z.object({
  pulsesyncOfficeId: z
    .string()
    .trim()
    .min(2)
    .max(80)
    .regex(/^[A-Za-z0-9._:-]+$/, 'Letters, digits and . _ : - only'),
  note: z.string().trim().max(200).optional(),
});
export type SupportAssignPulsesyncRequest = z.infer<typeof SupportAssignPulsesyncRequestSchema>;

/**
 * Server-side steps that C2G Admin cannot do (they edit files on the API
 * host), listed in the console after onboarding.
 */
export const SUPPORT_MANUAL_ONBOARDING_STEPS = [
  'Add the office key to /etc/velora/pulsesync-keys.json on the API host (CT 266).',
  'Set the office time mode in VELORA_PULSESYNC_TIME_MODES, then run velora-compute-push env and restart.',
  'Texting stays off: Warp 9 keeps sending until Zac approves the cut-over (Settings > Texting > Warp 9 hand-off).',
] as const;

/** Ending a support session: `restored` when the support account's own session came back. */
export const EndImpersonationResponseSchema = z.object({ restored: z.boolean() });
export type EndImpersonationResponse = z.infer<typeof EndImpersonationResponseSchema>;

/** One-time set-password links (create-support-account CLI). The token travels in the URL fragment. */
export const PASSWORD_SETUP_PATH = '/support/set-password';
export const PasswordSetupCheckSchema = z.object({ token: z.string().min(20).max(200) });
export const PasswordSetupPeekSchema = z.object({
  displayName: z.string(),
  /** Empty when the practice did not know the person's email (they choose it). */
  email: z.string(),
  /** The person must choose the email they will sign in with. */
  emailPending: z.boolean().optional(),
  /** Practice invites: the practice's name. Null for support links. */
  practiceName: z.string().nullable().optional(),
  /** `reset`: an office manager or Computers2Go reset the password (choose a new one). */
  purpose: z.enum(['support', 'invite', 'reset']).optional(),
  expiresAt: z.string().optional(),
  /** The username the person will sign in with (null when none was given yet). */
  username: z.string().nullable().optional(),
  /**
   * Invites: the person adds their own email and proves it with a code
   * (`/password-setup/email-code`) before the password is saved.
   */
  emailCodeRequired: z.boolean().optional(),
});
export type PasswordSetupPeek = z.infer<typeof PasswordSetupPeekSchema>;
export const PasswordSetupRequestSchema = PasswordSetupCheckSchema.extend({
  password: NewPasswordSchema,
  /** Invites: the email the code was sent to. Ignored for support links. */
  email: EmailSchema.optional(),
  /** Invites: the 6-digit code emailed to `email`. */
  emailCode: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter the 6-digit code from the email.')
    .optional(),
});
export const PasswordSetupResultSchema = z.object({
  ok: z.literal(true),
  /** The email to sign in with. */
  email: z.string(),
  /** The username to sign in with, when the account has one. */
  username: z.string().nullable().optional(),
  /**
   * Invites: the person is now signed in up to two-step sign-in set-up
   * (`mfa_enroll`): the app continues with the authenticator step.
   */
  next: z.enum(['sign_in', 'mfa_enroll']).optional(),
  csrfToken: z.string().optional(),
});

/** Invites: email a 6-digit code to the address the person typed. */
export const InviteEmailCodeRequestSchema = PasswordSetupCheckSchema.extend({
  email: EmailSchema,
});
export type InviteEmailCodeRequest = z.infer<typeof InviteEmailCodeRequestSchema>;
export const InviteEmailCodeResponseSchema = z.object({
  ok: z.literal(true),
  /** Minutes the code works for. */
  expiresInMinutes: z.number().int(),
  /**
   * False: the office cannot send email yet, so no code went out. Finish the
   * set-up without a code: the email is saved unverified (2026-11-01).
   */
  sent: z.boolean().optional(),
});
export type InviteEmailCodeResponse = z.infer<typeof InviteEmailCodeResponseSchema>;
/** Email codes for the first sign-in last this long. */
export const INVITE_EMAIL_CODE_MINUTES = 15;
export type PasswordSetupResult = z.infer<typeof PasswordSetupResultSchema>;

/**
 * Staff invites (W12): `/auth/invite#<token>` lets a person the practice added
 * set their password (and choose their sign-in email when the practice did
 * not know it). Uses the same public routes as the support set-password link.
 */
export const INVITE_PATH = '/auth/invite';
/** Placeholder sign-in emails of accounts made before the person chose one. */
export const PENDING_EMAIL_DOMAIN = 'invite.velora.invalid';
export const isPendingEmail = (email: string): boolean =>
  email.toLowerCase().endsWith(`@${PENDING_EMAIL_DOMAIN}`);
/** Invite links last this long by default (the server caps them at 30 days). */
export const INVITE_LINK_DAYS = 14;
export type PasswordSetupRequest = z.infer<typeof PasswordSetupRequestSchema>;

/* ------------------------------------------------------------------------ */
/* Admin: users, roles, offices, devices                                     */
/* ------------------------------------------------------------------------ */

export const RoleAssignmentSchema = z.object({
  roleId: z.uuid(),
  /** `null`: applies in every office the user can access. */
  locationId: z.uuid().nullable(),
});
export type RoleAssignment = z.infer<typeof RoleAssignmentSchema>;

/**
 * Step-up re-authentication for sensitive actions (full export MG-19, audit
 * export SA-26): the account password and, with two-step sign-in, a fresh
 * authenticator code or a recovery code.
 */
export const StepUpBodySchema = z.object({
  password: z.string().min(1).max(200),
  code: z.string().trim().min(6).max(32).optional(),
});
export type StepUpBody = z.infer<typeof StepUpBodySchema>;

export const PracticeUserSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  /** Short sign-in name (`mary.smith`); null when the person signs in by email only. */
  username: z.string().nullable().optional(),
  /** The person proved their email with a code (first sign-in). */
  emailVerified: z.boolean().optional(),
  displayName: z.string(),
  status: z.enum(['active', 'disabled']),
  isSupport: z.boolean(),
  /** A Computers2Go account (support, or the practice's own C2G Support user): pinned on top. */
  c2gAccount: z.boolean().optional(),
  /** False: no password set (invited, or a sign-in only through support). */
  hasPassword: z.boolean().optional(),
  /** Signs in with a temporary password and must choose their own at the next sign-in. */
  mustChangePassword: z.boolean().optional(),
  allOffices: z.boolean(),
  locationIds: z.array(z.uuid()),
  roles: z.array(RoleAssignmentSchema),
  workingLocationId: z.uuid().nullable(),
  mfaEnrolled: z.boolean(),
  mfaRequired: z.boolean(),
  lastSignInAt: z.string().nullable(),
  /**
   * Sign-in lock (SA-16): `paused` for 5 minutes after 5 wrong passwords,
   * `locked` after 10 until a manager unlocks it. Omitted means not locked.
   */
  lockState: z.enum(['none', 'paused', 'locked']).optional(),
  lockedUntil: z.string().nullable().optional(),
  /**
   * Account set-up (W12). `invited`: no password yet (the person has not
   * used their invite); `emailPending`: the practice has no email for them
   * yet (a placeholder is stored and `email` is empty).
   */
  setup: z
    .object({
      state: z.enum(['set_up', 'invited']),
      emailPending: z.boolean(),
      invite: z
        .object({
          createdAt: z.string(),
          expiresAt: z.string(),
          expired: z.boolean(),
          sentAt: z.string().nullable(),
          sentChannel: z.enum(['email', 'link']).nullable(),
          sentTo: z.string().nullable(),
        })
        .nullable(),
    })
    .optional(),
});
export type PracticeUser = z.infer<typeof PracticeUserSchema>;

/** Add a person without a password: they set it up from an invite link. */
export const InviteUserRequestSchema = z.object({
  displayName: z.string().trim().min(1).max(120),
  /** Leave empty when the email is not known: the person chooses it when accepting. */
  email: EmailSchema.nullable().default(null),
  roles: z.array(RoleAssignmentSchema).min(1, 'Give the person at least one role.'),
  allOffices: z.boolean().default(false),
  locationIds: z.array(z.uuid()).default([]),
  /** Email the invite now (only when an email is given). Default: just make the link. */
  send: z.boolean().default(false),
  /** PMS roster rows (pms_staff ids) and providers this person is. */
  pmsStaffIds: z.array(z.uuid()).max(20).default([]),
  providerIds: z.array(z.uuid()).max(20).default([]),
  /** Job title for the staff profile (HR), e.g. "Hygienist". */
  title: z.string().trim().min(1).max(80).nullable().default(null),
  /** Username to give; default first.last from the name (a number is added on a clash). */
  username: UsernameSchema.optional(),
});
export type InviteUserRequest = z.input<typeof InviteUserRequestSchema>;

export const InviteDeliverySchema = z.enum(['link', 'sent', 'held', 'failed']);
export type InviteDelivery = z.infer<typeof InviteDeliverySchema>;

export const InviteLinkSchema = z.object({
  /** Path + fragment (`/auth/invite#<token>`): shown once to the admin. */
  invitePath: z.string(),
  expiresAt: z.string(),
  delivery: InviteDeliverySchema,
  message: z.string(),
});
export type InviteLink = z.infer<typeof InviteLinkSchema>;

export const InviteUserResponseSchema = z.object({
  user: PracticeUserSchema,
  invite: InviteLinkSchema,
});
export type InviteUserResponse = z.infer<typeof InviteUserResponseSchema>;

/** New invite link for a person who has not set up their account (burns the old one). */
export const ResendInviteRequestSchema = z.object({
  /** `link`: make a link to copy (nothing is sent); `email`: also email it. */
  action: z.enum(['link', 'email']),
  /** Set or correct the person's email first (only before they accept). */
  email: EmailSchema.optional(),
});
export type ResendInviteRequest = z.infer<typeof ResendInviteRequestSchema>;

/**
 * `POST /v1/identity/users/:id/reset-password` (users.manage) and the C2G
 * Admin twin: reset the password of someone who already set up their account.
 * `link`: a one-time reset link shown once; `email`: also emailed to their
 * real email; `temporary`: a temporary password shown once, which they must
 * change at their next sign-in. Every session of the person ends; two-step
 * sign-in is kept unless `alsoResetMfa`.
 */
export const ResetPasswordRequestSchema = z.object({
  mode: z.enum(['link', 'email', 'temporary']),
  alsoResetMfa: z.boolean().default(false),
});
export type ResetPasswordRequest = z.input<typeof ResetPasswordRequestSchema>;

export const ResetPasswordResponseSchema = z.object({
  mode: z.enum(['link', 'email', 'temporary']),
  /** Link modes: the reset link (path + fragment), shown once. */
  link: InviteLinkSchema.nullable(),
  /** `temporary`: the temporary password, shown once. */
  temporaryPassword: z.string().nullable(),
  sessionsEnded: z.number().int(),
  mfaReset: z.boolean(),
});
export type ResetPasswordResponse = z.infer<typeof ResetPasswordResponseSchema>;

/** PMS staff the practice can make accounts for (Dentrix / Open Dental / Eaglesoft users). */
export const PmsStaffOfficeSchema = z.object({
  locationId: z.uuid(),
  /** PMS short code (Dentrix provider id, OD abbreviation) or user name. */
  code: z.string().nullable(),
  pmsSystem: z.enum(['dentrix', 'opendental', 'eaglesoft']).nullable(),
  /** What the PMS says the person is (provider type or user group). */
  pmsRole: z.string().nullable(),
});
export const PmsStaffPersonSchema = z.object({
  key: z.string(),
  displayName: z.string(),
  suggestedRoleKey: z.string(),
  /** How the role was chosen (`provider_type`, `user_group`, `code`, `default`). */
  roleBasis: z.enum(['provider_type', 'user_group', 'code', 'default']),
  offices: z.array(PmsStaffOfficeSchema),
  providerIds: z.array(z.uuid()),
  pmsStaffIds: z.array(z.uuid()),
  /** The Velora account already made for this person, if any. */
  userId: z.uuid().nullable(),
  matchedBy: z.enum(['link', 'name']).nullable(),
});
export type PmsStaffPerson = z.infer<typeof PmsStaffPersonSchema>;
export const PmsStaffRosterSchema = z.object({
  people: z.array(PmsStaffPersonSchema),
  /** Rows left out as not a person (CORP, Temp, Admin, ...). */
  skipped: z.number().int(),
});
export type PmsStaffRoster = z.infer<typeof PmsStaffRosterSchema>;

export const CreateUserRequestSchema = z.object({
  email: EmailSchema,
  displayName: z.string().trim().min(1).max(120),
  /** Initial password the admin hands over; ignored when the person already has an account. */
  password: NewPasswordSchema,
  roles: z.array(RoleAssignmentSchema).min(1, 'Give the person at least one role.'),
  allOffices: z.boolean().default(false),
  locationIds: z.array(z.uuid()).default([]),
});
export type CreateUserRequest = z.input<typeof CreateUserRequestSchema>;

export const UpdateUserRequestSchema = z.object({
  displayName: z.string().trim().min(1).max(120).optional(),
  status: z.enum(['active', 'disabled']).optional(),
  roles: z.array(RoleAssignmentSchema).optional(),
  allOffices: z.boolean().optional(),
  locationIds: z.array(z.uuid()).optional(),
});
export type UpdateUserRequest = z.infer<typeof UpdateUserRequestSchema>;

/** An admin changes (or removes, with null) a person's username. Audited. */
export const UpdateUsernameRequestSchema = z.object({ username: UsernameSchema.nullable() });
export type UpdateUsernameRequest = z.infer<typeof UpdateUsernameRequestSchema>;

/* ------------------------------------------------------------------------ */
/* Module access per role (Hayden 2026-10-28 R1, R26)                         */
/* ------------------------------------------------------------------------ */

/**
 * Whole modules an admin can hide from a role, on top of its permissions
 * (built-in roles too, so access is set before any staff member signs in).
 *
 * A hidden module is enforced on the server in three ways:
 * - `permissions`: those keys are taken out of the role's effective
 *   permissions (`identity.principal`, `identity.user_has_permission`; the
 *   SQL twin is `identity.module_hides`, kept equal by a DB test). Every
 *   route, nav item and home card that needs one of them disappears.
 * - `apiPrefixes`: routes under these paths answer 403 `module_hidden`
 *   (pipeline), for modules that share a coarse permission with things the
 *   role keeps (call analytics vs call pops both read `comms.view`).
 * - `navModules` / `sections`: the shell leaves those nav items and page
 *   sections out.
 *
 * A person with several roles sees a module when ANY of their roles shows it.
 */
export interface RoleModuleDef {
  key: string;
  label: string;
  description: string;
  permissions: readonly string[];
  apiPrefixes: readonly string[];
  navModules: readonly string[];
  /** Page sections the shell hides (`<module>.<section>`). */
  sections: readonly string[];
}

const keysOf = (...areas: string[]): string[] =>
  PERMISSIONS.map((p) => p.key).filter((k) => areas.includes(k.split('.')[0] ?? ''));

export const ROLE_MODULES: readonly RoleModuleDef[] = [
  {
    key: 'analytics',
    label: 'Analytics and reports',
    description:
      'Insights boards, provider and hygiene production, reports, goals, office compare and the patient map.',
    permissions: ['reports.view', 'reports.compare', 'analytics.patient_map'],
    apiPrefixes: ['/v1/analytics', '/v1/office-reports', '/v1/council'],
    navModules: ['analytics', 'council'],
    sections: [],
  },
  {
    key: 'money',
    label: 'Production and money numbers',
    description:
      'Dollar figures everywhere: production, collections, balances, deposits and day close. Payments can still be taken if the role keeps the ledger.',
    permissions: ['money.view', 'money.close_day'],
    apiPrefixes: [],
    navModules: [],
    sections: [],
  },
  {
    key: 'call_analytics',
    label: 'Call analytics and AI call review',
    description:
      'Answer rates, staff call performance, call insights and the AI review of recordings. Call pops, the call log and missed calls stay.',
    permissions: [],
    apiPrefixes: ['/v1/phones/analytics', '/v1/phones/insights', '/v1/phones/answer-windows'],
    navModules: [],
    sections: ['phones.analytics', 'phones.ai', 'phones.insights', 'phones.kpis'],
  },
  {
    key: 'ask',
    label: 'Ask Velora (AI assistant)',
    description: 'The AI assistant, which can answer questions about production and patients.',
    permissions: [],
    apiPrefixes: ['/v1/assistant'],
    navModules: ['assistant'],
    sections: ['assistant.widget'],
  },
  {
    key: 'texting',
    label: 'Texting and inbox',
    description: 'Two-way patient texting and the shared inbox.',
    permissions: [],
    apiPrefixes: ['/v1/inbox'],
    navModules: ['inbox'],
    sections: [],
  },
  {
    key: 'phones',
    label: 'Phones and call pops',
    description: 'Call pops, the call log, missed calls, voicemail and recordings.',
    permissions: [],
    apiPrefixes: ['/v1/phones'],
    navModules: ['phones'],
    sections: [],
  },
  {
    key: 'forms',
    label: 'Forms',
    description: 'Patient forms, intake packets and the review queue.',
    permissions: [],
    apiPrefixes: ['/v1/forms'],
    navModules: ['forms'],
    sections: [],
  },
  {
    key: 'schedule',
    label: 'Schedule',
    description: 'The appointment book and the flow board.',
    permissions: keysOf('schedule'),
    apiPrefixes: [],
    navModules: [],
    sections: [],
  },
  {
    key: 'billing',
    label: 'Billing, ledger, claims and statements',
    description: 'Patient ledgers, payments, adjustments, claims and statements.',
    permissions: keysOf('ledger', 'claims'),
    apiPrefixes: ['/v1/statements'],
    navModules: ['statements'],
    sections: [],
  },
  {
    key: 'clinical',
    label: 'Clinical',
    description: 'Chart, perio, imaging, clinical notes and treatment plans.',
    permissions: keysOf('chart', 'perio', 'imaging', 'notes', 'txplan'),
    apiPrefixes: [],
    navModules: [],
    sections: [],
  },
];

export const ROLE_MODULE_KEYS = ROLE_MODULES.map((m) => m.key);
export const RoleModuleKeySchema = z.string().refine((k) => ROLE_MODULE_KEYS.includes(k), {
  message: 'Unknown module.',
});

/** Modules hidden for someone: those every one of their roles hides. */
export function moduleHiddenFor(hidden: readonly string[] | undefined, key: string): boolean {
  return hidden?.includes(key) === true;
}

/** True when a route path falls under a hidden module's API prefixes. */
export function apiPathHidden(hidden: readonly string[] | undefined, url: string): string | null {
  if (!hidden || hidden.length === 0) return null;
  for (const m of ROLE_MODULES) {
    if (!hidden.includes(m.key)) continue;
    if (m.apiPrefixes.some((p) => url === p || url.startsWith(`${p}/`))) return m.key;
  }
  return null;
}

/** Phones, tablets and the Android app (role "no sign-in on phones", Hayden R26). */
export function isMobileUserAgent(ua: string | null | undefined): boolean {
  if (!ua) return false;
  return /Android|iPhone|iPod|iPad|Mobile|Windows Phone|BlackBerry|IEMobile|Opera Mini|VeloraMobile/i.test(
    ua,
  );
}

export const RoleSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  isSystem: z.boolean(),
  mfaRequired: z.boolean(),
  allOffices: z.boolean(),
  supportOnly: z.boolean(),
  permissions: z.array(PermissionKeySchema),
  memberCount: z.number().int(),
  /** Modules hidden from this role (`ROLE_MODULES`). Absent from older servers. */
  hiddenModules: z.array(z.string()).optional(),
  /** False: people with only this role cannot sign in from a phone or tablet. */
  mobileSignIn: z.boolean().optional(),
  /** Two-step methods people with this role may use (Settings > Security). */
  mfaMethods: z.array(MfaMethodKindSchema).optional(),
});
export type Role = z.infer<typeof RoleSchema>;

export const RoleKeySchema = z.string().regex(/^[a-z][a-z0-9_]{1,40}$/);

export const CreateRoleRequestSchema = z.object({
  name: z.string().trim().min(1).max(60),
  description: z.string().trim().max(300).default(''),
  mfaRequired: z.boolean().default(false),
  permissions: z.array(PermissionKeySchema).default([]),
  hiddenModules: z.array(RoleModuleKeySchema).max(20).default([]),
  mobileSignIn: z.boolean().default(true),
});
export type CreateRoleRequest = z.input<typeof CreateRoleRequestSchema>;

export const UpdateRoleRequestSchema = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  description: z.string().trim().max(300).optional(),
  mfaRequired: z.boolean().optional(),
  permissions: z.array(PermissionKeySchema).optional(),
  /** Allowed on built-in roles too (not Owner or C2G Support). */
  hiddenModules: z.array(RoleModuleKeySchema).max(20).optional(),
  mobileSignIn: z.boolean().optional(),
  /** Settings > Security: the authenticator only, or text and email too. */
  mfaMethods: z.array(MfaMethodKindSchema).min(1).max(3).optional(),
});
export type UpdateRoleRequest = z.infer<typeof UpdateRoleRequestSchema>;

export const PermissionCatalogSchema = z.object({
  areas: z.array(
    z.object({
      area: z.string(),
      label: z.string(),
      permissions: z.array(
        z.object({
          key: PermissionKeySchema,
          label: z.string(),
          description: z.string(),
          sensitive: z.boolean(),
        }),
      ),
    }),
  ),
});
export type PermissionCatalog = z.infer<typeof PermissionCatalogSchema>;

export const OfficeAddressSchema = z.object({
  line1: z.string().trim().max(120).default(''),
  line2: z.string().trim().max(120).default(''),
  city: z.string().trim().max(80).default(''),
  region: z.string().trim().max(40).default(''),
  postalCode: z.string().trim().max(20).default(''),
});

export const OfficeSchema = OfficeSummarySchema.extend({
  phone: z.string().nullable(),
  address: OfficeAddressSchema,
  retiredAt: z.string().nullable(),
  /** People with access to this office. */
  memberCount: z.number().int(),
});
export type Office = z.infer<typeof OfficeSchema>;

export const ShortNameSchema = z.string().trim().min(1).max(24);

/** IANA zone name, validated against the runtime's zone database on the server. */
export const TimezoneSchema = z.string().min(3).max(64);

export const CreateOfficeRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  shortName: ShortNameSchema,
  timezone: TimezoneSchema,
  phone: z.string().trim().max(40).optional(),
  address: OfficeAddressSchema.optional(),
  /** Defaults to the next unused office colour. */
  colorToken: OfficeColorTokenSchema.optional(),
});
export type CreateOfficeRequest = z.input<typeof CreateOfficeRequestSchema>;

export const UpdateOfficeRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  shortName: ShortNameSchema.optional(),
  timezone: TimezoneSchema.optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  address: OfficeAddressSchema.optional(),
  colorToken: OfficeColorTokenSchema.optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});
export type UpdateOfficeRequest = z.infer<typeof UpdateOfficeRequestSchema>;

export const DeviceSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  profile: DeviceProfileSchema,
  locationId: z.uuid().nullable(),
  idleLockMinutes: z.number().int().nullable(),
  enrolledAt: z.string(),
  lastSeenAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  /** True for the workstation this request came from. */
  current: z.boolean(),
});
export type Device = z.infer<typeof DeviceSchema>;

/** A browser where the person ticked "Remember this device" (Security page). */
export const RememberedDeviceSchema = z.object({
  id: z.uuid(),
  createdAt: z.string(),
  /** Fixed: 30 days after createdAt. Using the device does not extend it. */
  expiresAt: z.string(),
  lastUsedAt: z.string().nullable(),
  /** "Chrome on Windows"; null when the browser sent no user agent. */
  userAgentSummary: z.string().nullable(),
  ip: z.string().nullable(),
  /** True for the browser this request came from. */
  current: z.boolean(),
});
export type RememberedDevice = z.infer<typeof RememberedDeviceSchema>;

export const RememberedDeviceListSchema = z.object({ devices: z.array(RememberedDeviceSchema) });
export type RememberedDeviceList = z.infer<typeof RememberedDeviceListSchema>;

export const ForgetDevicesResponseSchema = z.object({ forgotten: z.number().int() });
export type ForgetDevicesResponse = z.infer<typeof ForgetDevicesResponseSchema>;

/* ------------------------------------------------------------------------ */
/* Desktop call-pop keys (apps/desktop)                                      */
/* ------------------------------------------------------------------------ */

/**
 * Prefix of a desktop key. The desktop app sends it as
 * `Authorization: Bearer vdk_...`; the API accepts it only on the read-only
 * call-pop routes in {@link DESKTOP_KEY_ROUTES}.
 */
export const DESKTOP_KEY_PREFIX = 'vdk_';
/** The practice a fleet desktop key's request is for (C2G Admin call pops). */
export const DESKTOP_KEY_PRACTICE_HEADER = 'x-velora-practice';
/** Fixed lifetime from issue; using the key never extends it. */
export const DESKTOP_KEY_DAYS = 30;
export const DESKTOP_KEY_SECONDS = DESKTOP_KEY_DAYS * 24 * 60 * 60;
/** The only routes a desktop key opens (method and Fastify route pattern). */
export const DESKTOP_KEY_ROUTES = [
  'GET /v1/events',
  'GET /v1/phones/live',
  'GET /v1/phones/lookup',
] as const;
/**
 * The only permissions a desktop key carries (intersected with the person's
 * own). `money.view` (desktop 0.4.4, Zac 2026-11-02): the desktop pop shows
 * the same patient balance and insurance pending as the web pop, for people
 * who already see money; the routes stay the three read-only ones above.
 */
export const DESKTOP_KEY_PERMISSIONS = ['comms.view', 'patient.view', 'money.view'] as const;

/** `POST /v1/identity/me/desktop-keys`: from the desktop app, right after sign-in. */
export const DesktopKeyCreateRequestSchema = z.object({
  /** The workstation name ("Saginaw Front 1"); one live key per person and name. */
  label: z.string().trim().min(1).max(80),
  appVersion: z.string().trim().max(40).optional(),
  /**
   * The app can show call pops for every practice (desktop 0.4.3+). From a
   * Computers2Go support account at the C2G Admin level (not in a support
   * session) the server then mints a fleet key: see DESKTOP_KEY_PRACTICE_HEADER.
   */
  supportFleet: z.boolean().optional(),
});
export type DesktopKeyCreateRequest = z.input<typeof DesktopKeyCreateRequestSchema>;

export const DesktopKeyCreateResponseSchema = z.object({
  id: z.uuid(),
  /** Shown once; the API stores only its SHA-256. */
  key: z.string().startsWith(DESKTOP_KEY_PREFIX),
  expiresAt: z.string(),
  userId: z.uuid(),
  practiceId: z.uuid(),
  /**
   * A fleet key (C2G support at the C2G Admin level): the practices it pops
   * calls for. Each request names one with DESKTOP_KEY_PRACTICE_HEADER and
   * acts as that practice's Computers2Go Support user, with the support
   * account as impersonator (audited in that practice under both names).
   */
  fleet: z
    .object({
      practices: z.array(
        z.object({
          id: z.uuid(),
          name: z.string(),
          offices: z.array(z.object({ id: z.uuid(), name: z.string() })),
        }),
      ),
    })
    .optional(),
});
export type DesktopKeyCreateResponse = z.infer<typeof DesktopKeyCreateResponseSchema>;

export const DesktopKeySchema = z.object({
  id: z.uuid(),
  label: z.string(),
  appVersion: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type DesktopKey = z.infer<typeof DesktopKeySchema>;

export const DesktopKeyListSchema = z.object({ keys: z.array(DesktopKeySchema) });
export type DesktopKeyList = z.infer<typeof DesktopKeyListSchema>;

export const EnrollDeviceRequestSchema = z.object({
  name: z.string().trim().min(1).max(80),
  profile: DeviceProfileSchema,
  locationId: z.uuid().nullable(),
  idleLockMinutes: z.number().int().min(1).max(240).nullable().default(null),
});
export type EnrollDeviceRequest = z.input<typeof EnrollDeviceRequestSchema>;

/* ------------------------------------------------------------------------ */
/* C2G Admin v2: the support dashboard (support accounts only)              */
/* ------------------------------------------------------------------------ */

/** Red / amber / green for an office or practice; `none` when nothing is linked. */
export const SupportRagSchema = z.enum(['green', 'amber', 'red', 'none']);
export type SupportRag = z.infer<typeof SupportRagSchema>;

export const SupportOfficeHealthSchema = z.object({
  locationId: z.uuid(),
  name: z.string(),
  shortName: z.string(),
  active: z.boolean(),
  patientsTotal: z.number().int(),
  patientsActive: z.number().int(),
  sync: z
    .object({
      adapter: z.string(),
      pmsSystem: z.string(),
      pulsesyncOfficeId: z.string().nullable(),
      connector: z.string(),
      lastHeartbeatAt: z.string().nullable(),
      adapterVersion: z.string().nullable(),
      reportedPmsVersion: z.string().nullable(),
      rootCause: z.string().nullable(),
      lastSuccessAt: z.string().nullable(),
      lastRunAt: z.string().nullable(),
      failedRuns24h: z.number().int(),
      openIssues: z.number().int(),
      openConflicts: z.number().int(),
      writesPaused: z.boolean(),
    })
    .nullable(),
  texting: z.object({
    a2pStatus: z.string().nullable(),
    lineA2pStatus: z.string().nullable(),
    smsLastErrorCode: z.string().nullable(),
    smsLastErrorAt: z.string().nullable(),
    optedOut: z.number().int(),
    sentThisMonth: z.number().int(),
    failedThisMonth: z.number().int(),
    warp9SentThisMonth: z.number().int(),
    warp9LastImportAt: z.string().nullable(),
    warp9HistoryImported: z.boolean(),
  }),
  /** Server-side PulseSync setup (never the key itself). Null when the office is not on PulseSync. */
  server: z.object({ keyOnFile: z.boolean(), timeMode: z.string().nullable() }).nullable(),
  rag: SupportRagSchema,
  ragReasons: z.array(z.string()),
});
export type SupportOfficeHealth = z.infer<typeof SupportOfficeHealthSchema>;

export const SupportBillingLineSchema = z.object({
  moduleKey: z.string(),
  moduleName: z.string(),
  officeName: z.string().nullable(),
  status: z.string(),
  source: z.string(),
  /** Today's catalog list price per office per month (what the module is worth). */
  priceCents: z.number().int(),
  /** The catalog price is still a placeholder the owner has not set. */
  placeholderPrice: z.boolean(),
  /** Counted in the monthly totals (active, scheduled to cancel or past due). */
  billable: z.boolean(),
  /**
   * What the monthly invoice actually charges for this line: the locked or
   * list price for a paid (Square) module, 0 for a grant, trial or internal
   * module; an annual prepay as its monthly equivalent.
   */
  actualCents: z.number().int(),
  /** Granted at no charge (source grant/internal while counted). */
  comped: z.boolean(),
  trialEndsAt: z.string().nullable(),
});
export type SupportBillingLine = z.infer<typeof SupportBillingLineSchema>;

/** How a practice pays for its modules: everything granted, some, all paid, or nothing on. */
export const SupportCompStatusSchema = z.enum(['comped', 'partial', 'paying', 'none']);
export type SupportCompStatus = z.infer<typeof SupportCompStatusSchema>;

export const SupportMrrMonthSchema = z.object({
  /** YYYY-MM (UTC). */
  month: z.string(),
  theoreticalCents: z.number().int(),
  actualCents: z.number().int(),
});
export type SupportMrrMonth = z.infer<typeof SupportMrrMonthSchema>;

/**
 * Read-only module revenue of a practice. Theoretical is every counted module
 * at today's list price, per office; actual is what invoices charge; comp is
 * the difference. Nothing here charges, invoices or changes a plan.
 */
export const SupportBillingSchema = z.object({
  lines: z.array(SupportBillingLineSchema),
  /** Same as `theoreticalCents` (kept for older clients). */
  monthlyCents: z.number().int(),
  theoreticalCents: z.number().int(),
  actualCents: z.number().int(),
  compCents: z.number().int(),
  comp: SupportCompStatusSchema,
  /** What Velora replaces for the practice (support's note), e.g. Warp 9 at 200 USD. */
  replaces: z.object({ label: z.string(), monthlyCents: z.number().int().nullable() }).nullable(),
  /** Theoretical and billed MRR by month, oldest first, up to 12 months ending this month. */
  history: z.array(SupportMrrMonthSchema),
});
export type SupportBilling = z.infer<typeof SupportBillingSchema>;

export const SupportReplacesRequestSchema = z.object({
  /** Empty or null clears the note. */
  label: z.string().trim().max(120).nullable(),
  monthlyCents: z.number().int().min(0).max(100_000_000).nullable(),
});
export type SupportReplacesRequest = z.infer<typeof SupportReplacesRequestSchema>;

export const SupportChecklistItemSchema = z.object({
  key: z.string(),
  label: z.string(),
  state: z.enum(['done', 'todo', 'manual', 'na']),
  detail: z.string(),
});
export type SupportChecklistItem = z.infer<typeof SupportChecklistItemSchema>;

export const SupportPracticeSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  status: z.string(),
  timezone: z.string(),
  createdAt: z.string(),
  contact: z.object({
    displayName: z.string().nullable(),
    adminEmail: z.string().nullable(),
    phone: z.string().nullable(),
    address: z
      .object({
        line1: z.string(),
        line2: z.string(),
        city: z.string(),
        region: z.string(),
        postalCode: z.string(),
      })
      .nullable(),
    billingEmail: z.string().nullable(),
    owners: z.array(z.object({ name: z.string(), email: z.string() })),
  }),
  officeCount: z.number().int(),
  patientsTotal: z.number().int(),
  patientsActive: z.number().int(),
  appointmentsNext30d: z.number().int(),
  appointmentsLast30d: z.number().int(),
  staffCount: z.number().int(),
  activeUsers7d: z.number().int(),
  lastStaffSignInAt: z.string().nullable(),
  billing: SupportBillingSchema,
  /** Texting and AI usage: cost, client price and margin by month (reporting only). */
  usage: SupportUsageSchema,
  storage: z.object({
    imagingBytes: z.number(),
    documentBytes: z.number(),
    otherBytes: z.number(),
    totalBytes: z.number(),
    addedByMonth: z.array(z.object({ month: z.string(), bytes: z.number() })),
  }),
  offices: z.array(SupportOfficeHealthSchema),
  noteCount: z.number().int(),
  rag: SupportRagSchema,
  checklist: z.array(SupportChecklistItemSchema),
});
export type SupportPracticeSummary = z.infer<typeof SupportPracticeSummarySchema>;

export const SupportAlertSchema = z.object({
  severity: z.enum(['critical', 'warning']),
  kind: z.enum(['sync_stale', 'connector_offline', 'texting_failures', 'storage_spike']),
  practiceId: z.uuid(),
  practiceName: z.string(),
  officeName: z.string().nullable(),
  message: z.string(),
});
export type SupportAlert = z.infer<typeof SupportAlertSchema>;

export const SupportDashboardSchema = z.object({
  practices: z.array(SupportPracticeSummarySchema),
  /** Practices whose summary could not be built this time (the rest still show). */
  unavailable: z.array(z.object({ id: z.uuid(), name: z.string() })),
  alerts: z.array(SupportAlertSchema),
  generatedAt: z.string(),
});
export type SupportDashboard = z.infer<typeof SupportDashboardSchema>;

export const SupportUserSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  email: z.string(),
  status: z.enum(['active', 'disabled']),
  isSupport: z.boolean(),
  roles: z.array(z.object({ id: z.uuid(), key: z.string(), name: z.string() })),
  officeIds: z.array(z.uuid()),
  allOffices: z.boolean(),
  lastSignInAt: z.string().nullable(),
  mfaEnrolled: z.boolean(),
  hasPassword: z.boolean(),
  /** Signs in with a temporary password and must choose their own at the next sign-in. */
  mustChangePassword: z.boolean().optional(),
  lockedUntil: z.string().nullable(),
  /** Other practices this sign-in opens (a password or MFA reset affects them too). */
  otherPractices: z.number().int(),
  /** A Computers2Go account (support, or the practice's own C2G Support user): pinned on top. */
  c2gAccount: z.boolean().optional(),
  /** The practice's own "Computers2Go Support" user (entered through a support session). */
  c2gOwner: z.boolean().optional(),
});
export type SupportUser = z.infer<typeof SupportUserSchema>;

/** `POST /v1/identity/support/practices/:id/c2g-support-user`: found or created. */
export const SupportC2gUserResponseSchema = z.object({
  userId: z.uuid(),
  displayName: z.string(),
  created: z.boolean(),
});
export type SupportC2gUserResponse = z.infer<typeof SupportC2gUserResponseSchema>;

/** `GET /v1/identity/support/users?q=`: a member of any practice. */
export const SupportFoundUserSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  email: z.string(),
  practiceId: z.uuid(),
  practiceName: z.string(),
  status: z.enum(['active', 'disabled']),
  isSupport: z.boolean(),
  c2gAccount: z.boolean(),
  roles: z.array(z.string()),
  offices: z.array(z.string()),
  canSignInAs: z.boolean(),
});
export type SupportFoundUser = z.infer<typeof SupportFoundUserSchema>;
export const SupportFindUsersQuerySchema = z.object({ q: z.string().trim().min(2).max(80) });

export const SupportUpdateMemberRequestSchema = z
  .object({
    status: z.enum(['active', 'disabled']).optional(),
    roleIds: z.array(z.uuid()).min(1).optional(),
    allOffices: z.boolean().optional(),
    locationIds: z.array(z.uuid()).optional(),
  })
  .refine((b) => b.status !== undefined || b.roleIds !== undefined, {
    message: 'Change the status or the roles and offices.',
  });
export type SupportUpdateMemberRequest = z.infer<typeof SupportUpdateMemberRequestSchema>;

/** Unlock and two-step reset change the account in every practice: say why. */
export const SupportAccountActionRequestSchema = z.object({
  reason: z.string().trim().min(5, 'Say why (a ticket number or a few words).').max(500),
});
export type SupportAccountActionRequest = z.infer<typeof SupportAccountActionRequestSchema>;

/**
 * `POST /v1/identity/support/practices/:id/users/:userId/invite`: a new
 * one-time set-up link for someone with no password (burns the old one).
 * `link`: shown once to support; `email`: also emailed to their real email.
 * Responds with InviteLinkSchema.
 */
export const SupportInviteRequestSchema = SupportAccountActionRequestSchema.extend({
  action: z.enum(['link', 'email']),
});
export type SupportInviteRequest = z.infer<typeof SupportInviteRequestSchema>;

/** `POST /v1/identity/support/practices/:id/users/:userId/reset-password`. */
export const SupportResetPasswordRequestSchema = SupportAccountActionRequestSchema.extend({
  mode: z.enum(['link', 'email', 'temporary']),
  alsoResetMfa: z.boolean().default(false),
});
export type SupportResetPasswordRequest = z.input<typeof SupportResetPasswordRequestSchema>;

/** `PUT .../users/:userId/email`: the real email of someone who has not set up yet. */
export const SupportSetEmailRequestSchema = SupportAccountActionRequestSchema.extend({
  email: EmailSchema,
});
export type SupportSetEmailRequest = z.infer<typeof SupportSetEmailRequestSchema>;

export const SupportAuditEventSchema = z.object({
  occurredAt: z.string(),
  action: z.string(),
  actorName: z.string(),
  impersonatorName: z.string().nullable(),
  entityType: z.string(),
  reason: z.string().nullable(),
  via: z.string().nullable(),
});
export type SupportAuditEvent = z.infer<typeof SupportAuditEventSchema>;

export const SupportNoteSchema = z.object({
  id: z.uuid(),
  body: z.string(),
  createdAt: z.string(),
  authorName: z.string(),
  mine: z.boolean(),
});
export type SupportNote = z.infer<typeof SupportNoteSchema>;

export const SupportNoteRequestSchema = z.object({ body: z.string().trim().min(1).max(4000) });

/* ------------------------------------------------------------------------ */
/* Velora for Android: push devices (apps/android)                          */
/* ------------------------------------------------------------------------ */

/**
 * What a phone push can say. The push carries ONLY this word; the app shows a
 * fixed text for it ("New message, open Velora") and never anything from the
 * server, so no PHI can reach Google or a lock screen.
 */
export const MOBILE_PUSH_CATEGORIES = ['message', 'call', 'missed_call', 'task'] as const;
export const MobilePushCategorySchema = z.enum(MOBILE_PUSH_CATEGORIES);
export type MobilePushCategory = z.infer<typeof MobilePushCategorySchema>;

/** A registration lasts this long from the last sign-in in the app. */
export const MOBILE_DEVICE_DAYS = 30;
export const MOBILE_DEVICE_SECONDS = MOBILE_DEVICE_DAYS * 24 * 60 * 60;

/**
 * Refusal reason when a person's role may not use Velora on a phone (the
 * role-based mobile restriction). The Android app and the web app both show
 * the server's message and stay signed out.
 */
export const MOBILE_NOT_ALLOWED_REASON = 'mobile_not_allowed';

/** `POST /v1/identity/me/mobile-devices`: the Android app, right after sign-in. */
export const MobileDeviceRegisterRequestSchema = z.object({
  platform: z.literal('android'),
  /** The Firebase Cloud Messaging registration token. */
  token: z.string().trim().min(20).max(4096),
  appVersion: z.string().trim().max(40).optional(),
  categories: z.array(MobilePushCategorySchema).max(4).optional(),
});
export type MobileDeviceRegisterRequest = z.input<typeof MobileDeviceRegisterRequestSchema>;

export const MobileDeviceRegisterResponseSchema = z.object({
  id: z.uuid(),
  expiresAt: z.string(),
  categories: z.array(MobilePushCategorySchema),
});
export type MobileDeviceRegisterResponse = z.infer<typeof MobileDeviceRegisterResponseSchema>;

/** `POST /v1/identity/me/mobile-devices/unregister`: sign-out in the app. */
export const MobileDeviceUnregisterRequestSchema = z.object({
  token: z.string().trim().min(20).max(4096),
});
export type MobileDeviceUnregisterRequest = z.input<typeof MobileDeviceUnregisterRequestSchema>;

export const MobileDeviceSchema = z.object({
  id: z.uuid(),
  platform: z.literal('android'),
  appVersion: z.string().nullable(),
  categories: z.array(MobilePushCategorySchema),
  createdAt: z.string(),
  lastRegisteredAt: z.string(),
  expiresAt: z.string(),
  lastPushAt: z.string().nullable(),
});
export type MobileDevice = z.infer<typeof MobileDeviceSchema>;

export const MobileDeviceListSchema = z.object({ devices: z.array(MobileDeviceSchema) });
export type MobileDeviceList = z.infer<typeof MobileDeviceListSchema>;

/**
 * `GET /v1/identity/mobile/config` (public): Firebase client settings for the
 * app. These are public client identifiers (they ship inside every Firebase
 * app); the server's sending credential never leaves the server. `push` is
 * null until Computers2Go sets up the Firebase project.
 */
export const MobileConfigSchema = z.object({
  push: z
    .object({
      provider: z.literal('fcm'),
      projectId: z.string(),
      applicationId: z.string(),
      apiKey: z.string(),
      senderId: z.string(),
    })
    .nullable(),
});
export type MobileConfig = z.infer<typeof MobileConfigSchema>;
